// =============================================================================
// SecretsReference — super-admin /admin/secrets-reference
//
// Full-page replacement for the previous "Secrets Reference" modal that
// lived inside the AI Settings page. The catalogue had outgrown a modal
// (4 required + 3 manageable overrides + 11 social/AI/video secrets +
// 3 push secrets + 5 pg_cron jobs) and was uncomfortable to scroll
// inside a 92vh dialog. As a real page it gets full-width layout, deep-
// linkable URL, and back-nav.
//
// Data is fetched here independently so the page can be opened directly
// (e.g. from a deep link, or after a fresh sign-in). It uses the same
// fetchPlatformAISettings + fetchAppSecretStatus + unsetAppSecret helpers
// the AI Settings page uses, so behaviour matches exactly.
//
// Row components (SecretRow, ManageableSecretRow) are exported from
// AISettings.tsx and imported here — single source of truth for the
// row markup + Unset interaction.
// =============================================================================

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Loader2, Check, AlertTriangle, KeyRound, ShieldAlert, RefreshCw, Clock,
  ChevronLeft,
} from 'lucide-react';
import { cn } from '@/utils/helpers';
import {
  fetchPlatformAISettings,
  fetchAppSecretStatus,
  unsetAppSecret,
  type PlatformAISettings,
  type ManageableAppSecret,
  type AppSecretStatus,
} from '@/lib/api';
import { SecretRow, ManageableSecretRow } from '@/pages/super-admin/AISettings';

export function SecretsReferencePage() {
  const navigate = useNavigate();

  // Data hooks — same pattern as AISettingsPage.
  const [settings, setSettings] = useState<PlatformAISettings | null>(null);
  const [loading,  setLoading]  = useState(true);
  const [error,    setError]    = useState<string | null>(null);

  // App-DB secret status (configured? + per-secret set/unset flags + last unset audit).
  const [secretStatus, setSecretStatus] = useState<{
    configured: boolean;
    secrets:    AppSecretStatus[];
    error?:     string;
  } | null>(null);
  const [refreshingSecrets, setRefreshingSecrets] = useState(false);
  const [unsetting,         setUnsetting]         = useState<string | null>(null);

  const loadSecretStatus = useCallback(async () => {
    setRefreshingSecrets(true);
    try {
      const s = await fetchAppSecretStatus();
      setSecretStatus(s);
    } catch (e: any) {
      setSecretStatus({ configured: false, secrets: [], error: e?.message || 'Failed to fetch secret status' });
    } finally {
      setRefreshingSecrets(false);
    }
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await fetchPlatformAISettings();
      setSettings(data);
    } catch (e: any) {
      setError(e?.message || 'Failed to load platform settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleUnsetSecret = useCallback(async (name: ManageableAppSecret) => {
    setUnsetting(name);
    setError(null);
    try {
      const result = await unsetAppSecret(name);
      if ('error' in result) {
        setError(result.error ?? null);
      } else {
        // Refresh both — the secret-status list AND the settings row
        // (so the audit-trail columns repaint with the new unset_at).
        await Promise.all([loadSecretStatus(), load()]);
      }
    } catch (e: any) {
      setError(e?.message || 'Failed to unset secret');
    } finally {
      setUnsetting(null);
    }
  }, [loadSecretStatus, load]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { loadSecretStatus(); }, [loadSecretStatus]);

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 py-6 space-y-6">

      {/* Page header — back link + title block. Same visual rhythm as
          the Infrastructure page for consistency across super-admin. */}
      <header className="space-y-2">
        <button
          type="button"
          onClick={() => navigate('/admin/ai-settings')}
          className="inline-flex items-center gap-1.5 text-sm text-surface-600 hover:text-surface-900 transition-colors"
        >
          <ChevronLeft size={16} />
          <span>Back to AI Settings</span>
        </button>
        <div className="flex items-start gap-3">
          <div className="w-11 h-11 rounded-xl bg-primary/10 text-primary flex items-center justify-center flex-shrink-0">
            <KeyRound className="w-5 h-5" />
          </div>
          <div className="min-w-0">
            <h1 className="text-xl font-bold text-surface-900">Secrets Reference</h1>
            <p className="text-sm text-surface-600 mt-0.5">
              Edge-function environment variables on the App + Platform Supabase projects, plus the pg_cron schedules they drive.
            </p>
          </div>
        </div>
      </header>

      {error && (
        <div className="rounded-lg bg-red-50 border border-red-200 p-3 text-sm text-red-900 inline-flex items-start gap-2">
          <AlertTriangle size={14} className="mt-0.5 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {loading && !settings ? (
        <div className="flex items-center gap-2 text-sm text-surface-600">
          <Loader2 size={14} className="animate-spin" /> Loading…
        </div>
      ) : (
        <div className="bg-surface-0 rounded-2xl shadow-sm border border-surface-200 px-6 py-6 space-y-6">

          {/* ── Required on the APP project ──────────────────────── */}
          <div className="border-l-4 border-emerald-500 pl-4">
            <div className="flex items-center gap-2 mb-1">
              <Check size={16} className="text-emerald-600" />
              <h3 className="text-base font-bold text-surface-900">Required on App project</h3>
            </div>
            <p className="text-sm text-surface-700 mb-1 leading-snug">
              Set once on the <strong>App project</strong> Edge Function secrets (<code className="font-mono text-xs bg-surface-100 text-surface-900 px-1 py-0.5 rounded">generate-itinerary</code>). Without these, the trip generator can't run.
            </p>
            <p className="text-xs text-surface-600 mb-3 italic">
              Where: App Supabase → Edge Functions → generate-itinerary → Secrets.
            </p>
            <table className="w-full">
              <tbody>
                <SecretRow name="OPENAI_API_KEY"           desc="OpenAI sk-… key. Required whenever the active provider is OpenAI." />
                <SecretRow name="ANTHROPIC_API_KEY"        desc="Anthropic sk-ant-… key. Required whenever the active provider is Anthropic." />
                <SecretRow name="PLATFORM_SUPABASE_URL"    desc="URL of THIS Platform project. Lets the App edge function read settings written by this UI." />
                <SecretRow name="PLATFORM_SERVICE_ROLE_KEY" desc="Service-role key of THIS Platform project. Auth for the cross-DB read above." />
              </tbody>
            </table>
          </div>

          {/* ── Required on the PLATFORM project ─────────────────── */}
          <div className={cn(
            'border-l-4 pl-4',
            secretStatus?.configured ? 'border-emerald-500' : 'border-amber-500',
          )}>
            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
              <div className="flex items-center gap-2">
                {secretStatus?.configured
                  ? <Check size={16} className="text-emerald-600" />
                  : <ShieldAlert size={16} className="text-amber-600" />
                }
                <h3 className="text-base font-bold text-surface-900">
                  Required on Platform project
                </h3>
                {secretStatus !== null && (
                  <span className={cn(
                    'inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold px-1.5 py-0.5 rounded border',
                    secretStatus.configured
                      ? 'bg-emerald-100 text-emerald-800 border-emerald-200'
                      : 'bg-amber-100 text-amber-800 border-amber-200',
                  )}>
                    {secretStatus.configured ? 'Configured' : 'Not configured'}
                  </span>
                )}
              </div>
            </div>
            <p className="text-sm text-surface-700 mb-1 leading-snug">
              Required for the <strong>Refresh + Unset</strong> features below to work. Without these, you can still see the AI Settings UI, but the "Set / Unset" status on the override secrets won't render and the buttons will be disabled.
            </p>
            <p className="text-xs text-surface-600 mb-3 italic">
              Where: <strong>Platform Supabase</strong> → Edge Functions → manage-app-secrets → Secrets.
            </p>
            <table className="w-full">
              <tbody>
                <SecretRow
                  name="SB_MGMT_TOKEN"
                  desc="Personal Access Token from supabase.com/dashboard/account/tokens. Used to call Supabase Management API. Generate with admin scope on the App project. (Cannot be named SUPABASE_* — that prefix is reserved by the Supabase platform.)"
                />
                <SecretRow
                  name="APP_SUPABASE_URL"
                  desc="Full App project URL (https://<ref>.supabase.co). PREFERRED — already used by other Platform functions like send-campaign + sync-usage-metrics. The function parses the ref from the hostname."
                />
              </tbody>
            </table>
            <p className="text-xs text-surface-600 mt-2 italic leading-snug">
              Alternative: set <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">APP_SUPABASE_PROJECT_REF</code> directly (20-char project ref) if APP_SUPABASE_URL points to a custom domain. The explicit ref wins when both are set.
            </p>
            {!secretStatus?.configured && secretStatus !== null && (
              <div className="mt-3 rounded-md bg-amber-50 border border-amber-200 p-3 text-sm text-surface-900 leading-snug">
                <strong>Status:</strong> One or both of these are missing. Set them on Platform → manage-app-secrets, then click Refresh below.
                {secretStatus.error && <div className="mt-1 text-surface-600 text-xs">Error: {secretStatus.error}</div>}
              </div>
            )}
          </div>

          {/* ── Optional overrides (manageable) ──────────────────── */}
          <div className="border-l-4 border-amber-500 pl-4">
            <div className="flex items-center justify-between gap-2 mb-1 flex-wrap">
              <div className="flex items-center gap-2">
                <ShieldAlert size={16} className="text-amber-600" />
                <h3 className="text-base font-bold text-surface-900">Optional overrides</h3>
              </div>
              <button
                type="button"
                onClick={loadSecretStatus}
                disabled={refreshingSecrets}
                title="Re-check from Supabase"
                className="inline-flex items-center gap-1.5 text-xs text-surface-700 hover:text-surface-900 px-2 py-1 rounded-md hover:bg-surface-100 transition-colors disabled:opacity-50"
              >
                <RefreshCw size={12} className={refreshingSecrets ? 'animate-spin' : ''} />
                <span className="font-medium">Refresh status</span>
              </button>
            </div>
            <p className="text-sm text-surface-700 mb-3 leading-snug">
              When any of these is set on the App edge function, it <strong>overrides</strong> the corresponding UI value. Three are manageable from this page; the others must be unset from the Supabase dashboard.
            </p>

            {!secretStatus && (
              <div className="text-sm text-surface-600 italic flex items-center gap-2 mb-3">
                <Loader2 size={14} className="animate-spin" /> Checking secret status…
              </div>
            )}

            {secretStatus?.configured && (
              <div className="space-y-2 mb-4">
                <ManageableSecretRow
                  name="ITINERARY_AI_PROVIDER"
                  desc="Forces 'openai' or 'anthropic'. Bypasses the Active provider toggle on the main page."
                  isSet={!!secretStatus.secrets.find(s => s.name === 'ITINERARY_AI_PROVIDER')?.set}
                  unsetAt={settings?.itinerary_provider_secret_unset_at ?? null}
                  unsetByUser={settings?.itinerary_provider_secret_unset_by ?? null}
                  busy={unsetting === 'ITINERARY_AI_PROVIDER'}
                  onUnset={() => handleUnsetSecret('ITINERARY_AI_PROVIDER')}
                  onRefresh={loadSecretStatus}
                  refreshing={refreshingSecrets}
                />
                <ManageableSecretRow
                  name="ITINERARY_PARALLEL_MODE"
                  desc="Forces 'per_city', 'on', or 'off'. Bypasses the Generation strategy dropdown."
                  isSet={!!secretStatus.secrets.find(s => s.name === 'ITINERARY_PARALLEL_MODE')?.set}
                  unsetAt={settings?.itinerary_parallel_secret_unset_at ?? null}
                  unsetByUser={settings?.itinerary_parallel_secret_unset_by ?? null}
                  busy={unsetting === 'ITINERARY_PARALLEL_MODE'}
                  onUnset={() => handleUnsetSecret('ITINERARY_PARALLEL_MODE')}
                  onRefresh={loadSecretStatus}
                  refreshing={refreshingSecrets}
                />
                <ManageableSecretRow
                  name="ANTHROPIC_ITINERARY_MODEL"
                  desc="Use the date-pinned name if the alias isn't resolving."
                  isSet={!!secretStatus.secrets.find(s => s.name === 'ANTHROPIC_ITINERARY_MODEL')?.set}
                  unsetAt={settings?.itinerary_anthropic_model_secret_unset_at ?? null}
                  unsetByUser={settings?.itinerary_anthropic_model_secret_unset_by ?? null}
                  busy={unsetting === 'ANTHROPIC_ITINERARY_MODEL'}
                  onUnset={() => handleUnsetSecret('ANTHROPIC_ITINERARY_MODEL')}
                  onRefresh={loadSecretStatus}
                  refreshing={refreshingSecrets}
                />
              </div>
            )}

            <div className="border-t border-surface-200 pt-3 mt-2">
              <p className="text-xs uppercase tracking-widest text-surface-600 font-bold mb-2">
                Other overrides — manage via Supabase dashboard
              </p>
              <table className="w-full">
                <tbody>
                  <SecretRow name="OPENAI_ITINERARY_MODEL"        desc="Forces an OpenAI model name not in the dropdown." />
                  <SecretRow name="ANTHROPIC_ITINERARY_MAX_TOKENS" desc="Forces a numeric max_tokens cap. Use only if the slider's range (4K–64K) isn't enough." />
                </tbody>
              </table>
            </div>
          </div>

          {/* ── Social publishing & video rendering ──────────────── */}
          <div className="border-l-4 border-indigo-500 pl-4">
            <div className="flex items-center gap-2 mb-1">
              <Check size={16} className="text-indigo-600" />
              <h3 className="text-base font-bold text-surface-900">Social publishing &amp; video rendering</h3>
            </div>
            <p className="text-sm text-surface-700 mb-1 leading-snug">
              Required only if the corresponding feature is live. Each integration's edge functions read these from the <strong>App project</strong>'s Edge Function secrets.
            </p>
            <p className="text-xs text-surface-600 mb-3 italic">
              Where: App Supabase → Edge Functions → [function name] → Secrets.
            </p>
            <table className="w-full">
              <tbody>
                <SecretRow name="META_APP_ID"             desc="Meta App ID from developers.facebook.com. Used by instagram-oauth-* + instagram-publish-reel." />
                <SecretRow name="META_APP_SECRET"         desc="Meta App Secret. Pair with META_APP_ID." />
                <SecretRow name="META_OAUTH_REDIRECT_URI" desc="https://<app-project-ref>.supabase.co/functions/v1/instagram-oauth-callback — must match the redirect URI registered with Meta." />

                <SecretRow name="TIKTOK_CLIENT_KEY"          desc="Client Key from developers.tiktok.com. Note: NOT 'client_id' — TikTok uses 'client_key'." />
                <SecretRow name="TIKTOK_CLIENT_SECRET"       desc="Client Secret. Pair with TIKTOK_CLIENT_KEY. Also used to HMAC the OAuth state." />
                <SecretRow name="TIKTOK_OAUTH_REDIRECT_URI"  desc="https://<app-project-ref>.supabase.co/functions/v1/tiktok-oauth-callback — must match the redirect URI registered with TikTok." />

                <SecretRow name="YOUTUBE_CLIENT_ID"          desc="OAuth 2.0 Client ID from console.cloud.google.com (Web application). YouTube Data API v3 must be enabled on the project." />
                <SecretRow name="YOUTUBE_CLIENT_SECRET"      desc="Client Secret. Used to HMAC the OAuth state and to exchange auth codes for tokens." />
                <SecretRow name="YOUTUBE_OAUTH_REDIRECT_URI" desc="https://<app-project-ref>.supabase.co/functions/v1/youtube-oauth-callback — must match the Authorized redirect URI in Google Cloud Console." />

                <SecretRow name="CREATOMATE_API_KEY"           desc="Creatomate render-API key. Used by generate-trip-reel to render the 9:16 trip reels." />
                <SecretRow name="CREATOMATE_WEBHOOK_SECRET"    desc="Random shared-secret string (any 32+ char value). Stamped into the webhook_url passed to Creatomate, then validated by creatomate-webhook so randoms can't forge render-completion POSTs. When unset, the system falls back to client-poll-only (Creatomate won't notify us when renders finish, so the reel row stays 'rendering' if the user closes the modal before Creatomate completes)." />
              </tbody>
            </table>
          </div>

          {/* ── Push notifications & background jobs (auth) ──────── */}
          <div className="border-l-4 border-rose-500 pl-4">
            <div className="flex items-center gap-2 mb-1">
              <Check size={16} className="text-rose-600" />
              <h3 className="text-base font-bold text-surface-900">Push notifications &amp; background jobs</h3>
            </div>
            <p className="text-sm text-surface-700 mb-3 leading-snug">
              Required for browser push, chat alerts, depart reminders, and live-trip ticks. Set on the <strong>App project</strong>.
            </p>
            <table className="w-full">
              <tbody>
                <SecretRow name="VAPID_PUBLIC_KEY"          desc="Web-push VAPID public key. Generate once with `npx web-push generate-vapid-keys`. Also exposed to the client as VITE_VAPID_PUBLIC_KEY in the build env." />
                <SecretRow name="VAPID_PRIVATE_KEY"         desc="Web-push VAPID private key. Pair with the public key above. Never exposed to the client." />
                <SecretRow name="CHAT_PUSH_SHARED_SECRET"   desc="Bearer token the trip_chat_messages INSERT trigger sends to notify-chat-message. Must match the value stored in Vault under chat_push_shared_secret." />
              </tbody>
            </table>
            <p className="text-xs text-surface-600 mt-2 italic leading-snug">
              <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">SUPABASE_SERVICE_ROLE_KEY</code> is auto-injected by Supabase on every edge function — used by send-push-to-user as a service-role bearer gate. No manual action needed.
            </p>
          </div>

          {/* ── Background jobs catalogue (pg_cron) ──────────────── */}
          <div className="border-l-4 border-cyan-500 pl-4">
            <div className="flex items-center gap-2 mb-1">
              <Clock size={16} className="text-cyan-600" />
              <h3 className="text-base font-bold text-surface-900">Background jobs (pg_cron)</h3>
            </div>
            <p className="text-sm text-surface-700 mb-3 leading-snug">
              Scheduled on the <strong>App project</strong> via <code className="font-mono text-xs bg-surface-100 text-surface-900 px-1 py-0.5 rounded">cron.schedule(...)</code>. Each job posts to the matching edge function with the service-role key.
            </p>
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-surface-200">
                  <th className="text-left py-1.5 pr-3 font-bold text-surface-900">Function</th>
                  <th className="text-left py-1.5 pr-3 font-bold text-surface-900">Cadence</th>
                  <th className="text-left py-1.5 font-bold text-surface-900">Purpose</th>
                </tr>
              </thead>
              <tbody className="text-surface-800">
                <tr className="border-b border-surface-100">
                  <td className="py-1.5 pr-3 font-mono text-xs">live-trip-tick</td>
                  <td className="py-1.5 pr-3">*/5 * * * *</td>
                  <td className="py-1.5">Now / next push for users currently on a trip; depart reminders.</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="py-1.5 pr-3 font-mono text-xs">flight-status-poll</td>
                  <td className="py-1.5 pr-3">*/15 * * * *</td>
                  <td className="py-1.5">AeroAPI status check on upcoming flights; pushes diff notifications.</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="py-1.5 pr-3 font-mono text-xs">send-notifications</td>
                  <td className="py-1.5 pr-3">0 9 * * *</td>
                  <td className="py-1.5">Daily 09:00 UTC digest — upcoming-trip / books-closing reminders.</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="py-1.5 pr-3 font-mono text-xs">notify-books-closing</td>
                  <td className="py-1.5 pr-3">0 10 * * *</td>
                  <td className="py-1.5">Daily 10:00 UTC — trips with books_close_at landing soon.</td>
                </tr>
                <tr>
                  <td className="py-1.5 pr-3 font-mono text-xs">anon-cleanup</td>
                  <td className="py-1.5 pr-3">0 3 * * *</td>
                  <td className="py-1.5">Daily 03:00 UTC — deletes guest accounts past their retention window.</td>
                </tr>
              </tbody>
            </table>
            <p className="text-xs text-surface-600 mt-3 italic leading-snug">
              See live state in <a href="/admin/infrastructure" className="font-semibold text-cyan-700 underline">Infrastructure</a> → Scheduled functions. If a row above is missing from that page, the cron isn't scheduled and the feature is silently off.
            </p>
          </div>

          {/* ── When to set each ─────────────────────────────────── */}
          <div className="border-l-4 border-surface-300 pl-4">
            <h3 className="text-base font-bold text-surface-900 mb-2">When should I set each?</h3>
            <ul className="text-sm text-surface-800 space-y-1.5 leading-snug">
              <li className="flex gap-2"><span className="text-surface-500">•</span><span><strong>Once at setup:</strong> all 4 required secrets above.</span></li>
              <li className="flex gap-2"><span className="text-surface-500">•</span><span><strong>Day-to-day:</strong> never. Use the AI Settings UI.</span></li>
              <li className="flex gap-2"><span className="text-surface-500">•</span><span><strong>Emergency rollback:</strong> set <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">ITINERARY_AI_PROVIDER</code> or <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">ITINERARY_PARALLEL_MODE</code> to pin a known-good config while you debug.</span></li>
              <li className="flex gap-2"><span className="text-surface-500">•</span><span><strong>Testing a model not in the dropdown:</strong> set the matching <code className="font-mono bg-surface-100 text-surface-900 px-1 py-0.5 rounded">*_ITINERARY_MODEL</code> secret. Once happy, add it to the codebase dropdown and remove the secret.</span></li>
              <li className="flex gap-2"><span className="text-surface-500">•</span><span><strong>After any override fix:</strong> <strong>delete</strong> the secret (don't set to empty). Otherwise the UI value never takes effect.</span></li>
            </ul>
          </div>

        </div>
      )}
    </div>
  );
}
